/* ============================================================
   VP BEST SELLERS

   Two jobs:

     1. reveal-on-scroll for every .vp-reveal in a VP Best Sellers
        section, on the same pattern the other vp-* sections use.

     2. the standing grid: seating the proof cards, holding the
        tail of the standing back, and releasing it two rows at a
        time.

   ON THE GRID. Liquid renders the plates in rank order and the
   proof cards as <template> elements beside them. It cannot place
   the cards, because where a card belongs depends on how many
   COLUMNS the grid has, and that is not known until the browser
   lays the page out.

   A card falls every CARD_EVERY cells — twelve by default. Twelve
   divides by every column count the grid uses, so the row interval
   falls out of the width:

       3 columns  ->  12/3 = every  4 rows  ->  rows  4,  8, 12
       2 columns  ->  12/2 = every  6 rows  ->  rows  6, 12, 18
       1 column   ->  12/1 = every 12 rows  ->  rows 12, 24, 36

   Three cards at every width, each landing at the same point in
   the sequence, so the standing reads the same whatever it is read
   on. Holding the interval in CELLS rather than rows is the whole
   trick: a row is three pieces on desktop and one on a phone, so a
   fixed count of rows would pause every 12 pieces at one width and
   every 4 at another.

   Cards also change SIDES — right edge, then left — so the pauses
   read as a rhythm down the page rather than as a stripe stacked
   in one column. A card's seat is (row-1)*cols + side, which means
   the same rhythm is a different cell ORDER at each width. So the
   grid is re-cut whenever the column count changes.
   ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- reveal on scroll ------------------------------------ */
  function observeReveals(scope) {
    var targets = scope.querySelectorAll('.vp-reveal:not(.in)');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window) || REDUCED) {
      Array.prototype.forEach.call(targets, function (el) { el.classList.add('in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });

    Array.prototype.forEach.call(targets, function (el) { io.observe(el); });

    /* backstop, as the other vp-* sections carry: nothing stays
       invisible because an observer never fired */
    window.setTimeout(function () {
      Array.prototype.forEach.call(targets, function (el) { el.classList.add('in'); });
    }, 3800);
  }

  /* ---- the gap under the header ----------------------------
     .vp-header is fixed: it covers the top of the page without
     occupying it, so the hero has to be pushed clear of it by hand.
     The category pages do that with a flat margin-top:155px.

     That literal cannot be reused here. It is the sum of several
     things — the header's resting height, minus the in-flow band the
     header group puts above main — and this page changes one of the
     terms by hiding the announcement bar. Every attempt to re-derive
     the number by arithmetic got it wrong, because each term is itself
     an estimate: rendered type, webfont swap, the countdown strip's
     padding.

     So stop computing it. MEASURE THE ERROR INSTEAD: compare where the
     hero actually starts against where the header actually ends, and
     correct the margin by the difference. Whatever sits above main and
     whatever the header really measures, the two edges are made to
     meet. Self-correcting, and it needs to know nothing about either.
     ---------------------------------------------------------- */
  function syncHeaderOffset() {
    var header  = document.querySelector('.vp-header');
    var section = document.querySelector('.vp-bs .bs-standing');
    if (!header || !section) return;

    /* Only correct at rest. The header is ~40px shorter once compact,
       so measuring mid-scroll would set a short margin and the hero
       would jump the moment you moved.

       Tested on scroll position rather than the .clear class: that
       class is applied by vp-header.js on the same DOMContentLoaded as
       this runs on, so whichever executed second decided whether the
       guard passed — the measurement was being skipped about half the
       time and the offset silently fell back to a hard-coded guess. */
    if ((window.scrollY || window.pageYOffset || 0) > 60) return;

    var headerBottom = header.getBoundingClientRect().bottom;
    var heroTop      = section.getBoundingClientRect().top;
    var drift        = heroTop - headerBottom;      /* >0 gap, <0 overlap */

    if (Math.abs(drift) < 0.5) return;              /* already flush */

    var current = parseFloat(window.getComputedStyle(section).marginTop) || 0;
    var corrected = Math.max(0, Math.round(current - drift));

    document.documentElement.style.setProperty('--bs-header-h', corrected + 'px');
  }

  function watchHeader() {
    if (!document.querySelector('.vp-bs')) return;   /* not this page */
    syncHeaderOffset();

    /* the wordmark is a webfont: the header's height changes when it
       swaps in, so measure again once fonts have settled */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(syncHeaderOffset).catch(function () {});
    }
    window.addEventListener('resize', syncHeaderOffset, { passive: true });

    /* Late passes. Layout is still settling at DOMContentLoaded — images
       above the fold, the header's own scripts, the webfont swap — and
       the theme's own offset script in theme.liquid hedges the same way
       with a 300ms re-run. Cheap, and it converges: once the two edges
       are flush the function returns without touching anything. */
    window.addEventListener('load', syncHeaderOffset);
    window.setTimeout(syncHeaderOffset, 300);
    window.setTimeout(syncHeaderOffset, 1200);

    /* the header collapses the announcement bar and re-lays out at
       breakpoints; watch it rather than poll */
    var header = document.querySelector('.vp-header');
    if (header && window.ResizeObserver) {
      new window.ResizeObserver(syncHeaderOffset).observe(header);
    }
  }

  /* ---- the standing grid ----------------------------------- */
  function initGrid(wrap) {
    if (!wrap || wrap.dataset.vpGridInit) return;
    wrap.dataset.vpGridInit = '1';

    var grid = wrap.querySelector('[data-bs-grid]');
    if (!grid) return;

    var foot = wrap.querySelector('[data-bs-more-foot]');
    var btn  = wrap.querySelector('[data-bs-more]');

    /* 0 means "no proof cards". A plain `|| 12` would swallow that,
       0 being falsy, and put the cards back. */
    var everyRaw = parseInt(grid.getAttribute('data-card-every'), 10);
    var EVERY = isNaN(everyRaw) ? 12 : everyRaw;
    var SHOWN = parseInt(grid.getAttribute('data-shown'), 10) || 6;

    /* how many rows a press releases, keyed by column count. Two
       rows is right on desktop, where a row is three pieces — but a
       row on a phone is ONE piece, and two of those is a press that
       barely moves. So rows rise as columns fall and every width
       lands on the same six cells per press. */
    var ROWS = { 1: 6, 2: 3, 3: 2, 4: 2 };

    /* taken once: these elements are re-ordered, never re-made */
    var plates = Array.prototype.slice.call(grid.querySelectorAll('.plate'));
    var cards  = Array.prototype.slice
      .call(wrap.querySelectorAll('template[data-bs-card]'))
      .map(function (t) { return t.innerHTML; });

    var shown = SHOWN;   /* cells revealed so far */
    var built = 0;       /* the column count the DOM is currently cut for */

    /* Read from the RESOLVED grid rather than from a media query,
       so the answer is whatever the browser actually laid out. */
    function columns() {
      var tracks = window.getComputedStyle(grid).gridTemplateColumns;
      var n = tracks ? tracks.split(/\s+/).filter(Boolean).length : 0;
      return n || 1;
    }

    function makeCard(html, isHeld) {
      var el = document.createElement('div');
      el.className = 'bs-proofcard vp-reveal' + (isHeld ? ' bs-held' : '');
      el.setAttribute('data-section', 'vp-proof-card');
      el.innerHTML = html;
      return el;
    }

    /* The foot is hidden rather than removed. Narrowing changes the
       cell count, so a foot that was finished can be needed again,
       and a removed one could not come back. */
    function syncFoot() {
      if (foot) foot.hidden = !grid.querySelector('.bs-held');
    }

    function recut() {
      var cols = columns();
      if (cols === built) return;

      var firstCut = built === 0;
      built = cols;

      var seating = EVERY > 0 && cards.length > 0;
      var step = seating ? Math.max(1, Math.round(EVERY / cols)) : 0;
      var frag = document.createDocumentFragment();
      var i = 0, k = 0, row = step, n = 0;

      while (i < plates.length) {
        /* right edge, then left, and so on — at one column there is
           no side to alternate, so a card takes the only cell there is */
        var side   = (cols === 1) ? 1 : (k % 2 === 0 ? cols : 1);
        var seat   = seating ? (row - 1) * cols + side : -1;
        var isHeld = n >= shown;

        /* a card is only ever seated while pieces remain, so none
           trails the end of the standing */
        if (seating && n + 1 === seat) {
          frag.appendChild(makeCard(cards[k % cards.length], isHeld));
          k++; row += step; n++;
          continue;
        }

        plates[i].classList.toggle('bs-held', isHeld);
        frag.appendChild(plates[i]);
        i++; n++;
      }

      grid.textContent = '';
      grid.appendChild(frag);

      if (firstCut) {
        /* first pass: let the observer bring these in as they are
           scrolled to, the way the rest of the page arrives */
        observeReveals(grid);
      } else {
        /* a re-cut is not an arrival — anything already on screen
           stays on screen rather than fading in again */
        Array.prototype.forEach.call(
          grid.querySelectorAll('.vp-reveal:not(.bs-held)'),
          function (el) { el.classList.add('in'); });
        observeReveals(grid);
      }
      syncFoot();
    }

    recut();
    syncFoot();

    if (btn) {
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();

        var cols  = columns();
        var batch = Array.prototype.slice.call(
          grid.querySelectorAll('.bs-held'), 0, (ROWS[cols] || 2) * cols);

        batch.forEach(function (el, i) {
          el.classList.remove('bs-held');
          /* next frame, so the browser has laid the cell out before
             the reveal transition is asked to run */
          window.requestAnimationFrame(function () {
            window.setTimeout(function () { el.classList.add('in'); }, i * 70);
          });
        });

        shown += batch.length;
        syncFoot();
      });
    }

    /* Watch the grid itself rather than the window: the column
       count is what matters, and only the grid knows it. A previous
       observer is dropped first, or a section reload in the theme
       editor would leave one behind holding a detached grid. */
    if (wrap.__bsResize) wrap.__bsResize.disconnect();
    if (window.ResizeObserver) {
      wrap.__bsResize = new window.ResizeObserver(recut);
      wrap.__bsResize.observe(grid);
    }
  }

  /* ---- boot ------------------------------------------------- */
  function init(root) {
    watchHeader();
    var scope = root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(
      scope.querySelectorAll('.vp-bs'), function (sec) { observeReveals(sec); });
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-bs-standing-grid]'), initGrid);
  }

  function boot() { init(document); }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
